import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE from '../config';

const RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '3:2', '2:3'];

function useVideoPoller(token, requestId, onDone, onError) {
  useEffect(() => {
    if (!requestId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/poll/${requestId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.success) { clearInterval(interval); onError(data.error); return; }
        if (data.status === 'done')   { clearInterval(interval); onDone(data.url); }
        if (data.status === 'failed') { clearInterval(interval); onError('영상 생성 실패'); }
      } catch (e) {
        clearInterval(interval);
        onError(e.message);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [requestId]);
}

function detectRatio(w, h) {
  const r = w / h;
  const list = [
    { l: '16:9', v: 16/9 }, { l: '9:16', v: 9/16 }, { l: '1:1', v: 1 },
    { l: '4:3', v: 4/3 }, { l: '3:4', v: 3/4 }, { l: '3:2', v: 3/2 }, { l: '2:3', v: 2/3 },
  ];
  return list.reduce((a, b) => Math.abs(r - a.v) < Math.abs(r - b.v) ? a : b).l;
}

export default function ImageToVideo({ onSave }) {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [resolution, setResolution] = useState('720p');
  const [duration, setDuration] = useState('10');
  const [ratio, setRatio] = useState('16:9');
  const [suggestedRatio, setSuggestedRatio] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  useVideoPoller(
    token,
    requestId,
    (url) => { setVideoUrl(url); setLoading(false); onSave({ type: 'i2v', prompt, url }); },
    (err) => { setError(err); setLoading(false); }
  );

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => {
      setImagePreview(ev.target.result);
      const img = new Image();
      img.onload = () => {
        const detected = detectRatio(img.width, img.height);
        setSuggestedRatio(detected);
        setRatio(detected);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!imageFile) return;
    setLoading(true);
    setError(null);
    setVideoUrl(null);
    setRequestId(null);
    setElapsed(0);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(imageFile);
      });
      const response = await fetch(`${API_BASE}/api/i2v`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ prompt: prompt || 'animate this image naturally', image: base64, resolution, duration, ratio }),
      });
      const data = await response.json();
      if (data.success) { setRequestId(data.request_id); }
      else { setError(data.error); setLoading(false); }
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const cancel = () => { setRequestId(null); setLoading(false); setElapsed(0); };

  const download = () => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `grok-i2v-${Date.now()}.mp4`;
    a.click();
  };

  const fmtTime = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-icon red">🎬</div>
        <div className="panel-title-group">
          <div className="panel-title">이미지 → 영상</div>
          <div className="panel-sub">이미지를 첫 프레임으로 영상 생성</div>
        </div>
      </div>
      <div className="panel-body">
        <div className="form-col">
          <div className="form-group">
            <label>이미지 업로드</label>
            <div
              className={`upload-area ${dragging ? 'dragging' : ''}`}
              style={{
                borderColor: dragging ? 'var(--accent2)' : undefined,
                background: dragging ? 'rgba(255,51,102,0.05)' : undefined,
              }}
              onClick={() => fileRef.current.click()}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
            >
              <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="upload-preview" />
              ) : (
                <>
                  <span className="upload-icon">🖼</span>
                  <span className="upload-text">클릭하거나 이미지를 드래그해서 올리세요</span>
                  <span className="upload-hint">PNG, JPG, WEBP</span>
                </>
              )}
            </div>
            {imagePreview && (
              <button className="remove-btn"
                onClick={() => { setImageFile(null); setImagePreview(null); setSuggestedRatio(null); }}>
                ✕ 이미지 제거
              </button>
            )}
          </div>

          <div className="form-group">
            <label>모션 프롬프트 (선택)</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="카메라가 천천히 줌인, 나뭇잎이 바람에 흔들림..."
              rows={3}
            />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>해상도</label>
              <select value={resolution} onChange={e => setResolution(e.target.value)}>
                <option value="480p">480p</option>
                <option value="720p">720p (최대)</option>
              </select>
            </div>
            <div className="form-group">
              <label>길이</label>
              <select value={duration} onChange={e => setDuration(e.target.value)}>
                <option value="5">5초</option>
                <option value="10">10초</option>
                <option value="15">15초 (최대)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>
              화면 비율
              {suggestedRatio && (
                <span style={{ color: 'var(--accent)', fontSize: '10px', marginLeft: '6px' }}>
                  ✦ 자동감지: {suggestedRatio}
                </span>
              )}
            </label>
            <div className="ratio-grid">
              {RATIOS.map(r => (
                <button
                  key={r}
                  className="ratio-btn"
                  onClick={() => setRatio(r)}
                  style={{
                    border: ratio === r ? '2px solid var(--accent2)' : '1px solid var(--border)',
                    background: ratio === r ? 'rgba(255,51,102,0.12)' : 'var(--bg3)',
                    color: ratio === r ? 'var(--accent2)' : r === suggestedRatio ? 'var(--accent)' : 'var(--text2)',
                    fontWeight: ratio === r ? '700' : '400',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {!loading ? (
            <button className="btn btn-red" onClick={generate} disabled={!imageFile}>
              ▶ 영상 생성
            </button>
          ) : (
            <button className="btn-cancel" onClick={cancel}>✕ 취소</button>
          )}
          {error && <div className="error-box">⚠ {error}</div>}
        </div>

        <div className="output-area">
          <span className="output-label">결과</span>
          <div className="output-box">
            {loading ? (
              <div className="loading">
                <div className="loading-timer">{fmtTime(elapsed)}</div>
                <div className="loading-bar">
                  <div className="loading-bar-fill" style={{ background: 'var(--accent2)' }} />
                </div>
                <span className="loading-text">영상 생성 중... (보통 1~4분 소요)</span>
              </div>
            ) : videoUrl ? (
              <video src={videoUrl} controls autoPlay loop className="output-video" />
            ) : (
              <div className="output-placeholder">
                <span className="output-placeholder-icon">🎬</span>
                <span className="output-placeholder-text">영상이 여기 표시됩니다</span>
              </div>
            )}
          </div>
          {videoUrl && <button className="btn-download" onClick={download}>↓ MP4 다운로드</button>}
        </div>
      </div>
    </div>
  );
}
